import { FC, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import { API_BASE } from "../../config/api";
import style from "./style.module.css";

interface ProductOption {
  product_id: number;
  product_name: string;
}

interface Props {
  value?: number;
  valueLabel?: string;
  onChange: (product: ProductOption) => void;
  placeholder?: string;
  className?: string;
}

// Products are the one entity in this app with a genuinely unbounded catalog
// size — a plain <select> fed by a page=1&limit=100 lookup (the pattern used
// for every other, genuinely-small entity like Category/Brand/Store) silently
// hides any product past the 100th. This searches the server as the user
// types instead of requiring the full catalog to be loaded client-side.
const ProductSelect: FC<Props> = ({ value, valueLabel, onChange, placeholder, className }) => {
  const [cookies] = useCookies();
  const [query, setQuery] = useState(valueLabel || "");
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const blurTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Keep the input showing the selected product's name when it changes
  // externally (e.g. opening the Edit modal for a different row) and the
  // dropdown isn't actively being edited.
  useEffect(() => {
    if (!isOpen) setQuery(valueLabel || "");
  }, [valueLabel, value, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ page: "1", limit: "20" });
        if (query.trim()) params.set("search", query.trim());
        const response = await authenticatedFetch(`${API_BASE}/product?${params.toString()}`, { method: "GET" }, cookies.auth);
        if (response.ok) {
          const data = await response.json();
          setOptions(Array.isArray(data?.data) ? data.data : []);
        }
      } catch (error) {
        console.error("Error searching products:", error);
      } finally {
        setIsLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, isOpen, cookies.auth]);

  const handleSelect = (option: ProductOption) => {
    onChange(option);
    setQuery(option.product_name);
    setIsOpen(false);
  };

  return (
    <div className={`${style.wrapper} ${className || ""}`}>
      <input
        type="text"
        value={query}
        placeholder={placeholder || "Search products by name..."}
        className={style.input}
        onFocus={() => setIsOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
        onBlur={() => { blurTimeout.current = setTimeout(() => setIsOpen(false), 150); }}
      />
      {isOpen && (
        <div className={style.dropdown} onMouseDown={(e) => { e.preventDefault(); clearTimeout(blurTimeout.current); }}>
          {isLoading && <div className={style.status}>Searching...</div>}
          {!isLoading && options.length === 0 && <div className={style.status}>No products found</div>}
          {!isLoading && options.map((option) => (
            <div
              key={option.product_id}
              className={option.product_id === value ? style.optionActive : style.option}
              onClick={() => handleSelect(option)}
            >
              {option.product_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductSelect;
